import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import {
  Mail,
  Plus,
  Copy,
  Trash2,
  RefreshCw,
  KeyRound,
  Paperclip,
  Clock,
  Inbox as InboxIcon,
  Loader2,
  FileText,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { mailApi, type MailInbox, type MailListItem, type MailMessage } from '@/api/mail'

function formatWhen(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const now = new Date()
  const sameDay =
    d.getDate() === now.getDate() &&
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear()
  if (sameDay) return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
}

function copy(text: string, label = 'Copiado') {
  navigator.clipboard.writeText(text).then(
    () => toast.success(label),
    () => toast.error('Não foi possível copiar'),
  )
}

export function Emails() {
  // ---- Inboxes ----
  const [inboxes, setInboxes] = useState<MailInbox[]>([])
  const [inboxesLoading, setInboxesLoading] = useState(true)
  const [selectedInboxId, setSelectedInboxId] = useState<number | null>(null)

  // ---- Messages ----
  const [messages, setMessages] = useState<MailListItem[]>([])
  const [messagesLoading, setMessagesLoading] = useState(false)

  // ---- Reading ----
  const [selectedMessageId, setSelectedMessageId] = useState<number | null>(null)
  const [message, setMessage] = useState<MailMessage | null>(null)
  const [messageLoading, setMessageLoading] = useState(false)

  // ---- New inbox dialog ----
  const [createOpen, setCreateOpen] = useState(false)
  const [createMode, setCreateMode] = useState<'random' | 'custom'>('random')
  const [customLocal, setCustomLocal] = useState('')
  const [customLabel, setCustomLabel] = useState('')
  const [creating, setCreating] = useState(false)

  // ---- Delete confirm ----
  const [deleteTarget, setDeleteTarget] = useState<MailInbox | null>(null)
  const [deleting, setDeleting] = useState(false)

  const selectedInbox = useMemo(
    () => inboxes.find((i) => i.id === selectedInboxId) || null,
    [inboxes, selectedInboxId],
  )

  const loadInboxes = async () => {
    try {
      const res = await mailApi.listInboxes()
      setInboxes(res.inboxes)
      // Auto-select first inbox if none selected
      setSelectedInboxId((prev) => {
        if (prev && res.inboxes.some((i) => i.id === prev)) return prev
        return res.inboxes[0]?.id ?? null
      })
    } catch {
      /* ignore transient */
    } finally {
      setInboxesLoading(false)
    }
  }

  // Refetch inbox list every 30s
  useEffect(() => {
    let active = true
    const poll = async () => {
      if (!active) return
      await loadInboxes()
      if (active) setTimeout(poll, 30000)
    }
    poll()
    return () => {
      active = false
    }
  }, [])

  // Load + poll messages for the selected inbox (10s)
  useEffect(() => {
    if (!selectedInboxId) {
      setMessages([])
      return
    }
    let active = true
    setMessagesLoading(true)
    const load = async (showLoading: boolean) => {
      try {
        const res = await mailApi.listMessages(selectedInboxId)
        if (!active) return
        setMessages(res.data)
      } catch {
        /* ignore */
      } finally {
        if (active && showLoading) setMessagesLoading(false)
      }
    }
    const poll = async () => {
      if (!active) return
      await load(false)
      if (active) setTimeout(poll, 10000)
    }
    load(true).then(() => {
      if (active) setTimeout(poll, 10000)
    })
    return () => {
      active = false
    }
  }, [selectedInboxId])

  // Load full message on selection
  useEffect(() => {
    if (!selectedMessageId) {
      setMessage(null)
      return
    }
    let active = true
    setMessageLoading(true)
    mailApi
      .getMessage(selectedMessageId)
      .then((res) => {
        if (!active) return
        setMessage(res.message)
        // mark as read locally
        setMessages((prev) =>
          prev.map((m) => (m.id === selectedMessageId ? { ...m, read_at: m.read_at ?? new Date().toISOString() } : m)),
        )
      })
      .catch(() => {
        if (active) toast.error('Não foi possível abrir a mensagem')
      })
      .finally(() => {
        if (active) setMessageLoading(false)
      })
    return () => {
      active = false
    }
  }, [selectedMessageId])

  const handleCreate = async () => {
    setCreating(true)
    try {
      const body: { address?: string; label?: string } = {}
      if (createMode === 'custom') {
        const local = customLocal.trim()
        if (!local) {
          toast.error('Digite um endereço')
          setCreating(false)
          return
        }
        body.address = local
      }
      if (customLabel.trim()) body.label = customLabel.trim()
      const res = await mailApi.createInbox(body)
      toast.success('Caixa criada')
      setCreateOpen(false)
      setCustomLocal('')
      setCustomLabel('')
      setCreateMode('random')
      await loadInboxes()
      setSelectedInboxId(res.inbox.id)
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Erro ao criar caixa'
      toast.error(msg)
    } finally {
      setCreating(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await mailApi.deleteInbox(deleteTarget.id)
      toast.success('Caixa apagada')
      if (selectedInboxId === deleteTarget.id) {
        setSelectedInboxId(null)
        setSelectedMessageId(null)
      }
      setDeleteTarget(null)
      await loadInboxes()
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Erro ao apagar')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 pt-4 pb-2 shrink-0">
        <Mail className="h-7 w-7 text-primary" />
        <div>
          <h1 className="text-lg font-bold text-foreground">Emails</h1>
          <p className="text-xs text-muted-foreground">
            Caixas <span className="text-primary">@blissf.com</span> — receba códigos de verificação
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            className="h-8 gap-1.5 border-primary/40 bg-primary/5 text-xs text-primary hover:bg-primary/10"
            onClick={() => {
              const token = localStorage.getItem('auth_token') || ''
              if (!token) {
                toast.error('Nenhum token encontrado. Faça login novamente.')
                return
              }
              navigator.clipboard.writeText(token)
              toast.success('🔑 Token de API copiado!')
            }}
            title="Copiar seu Token de Autenticação da API Blissf"
          >
            <KeyRound className="h-3.5 w-3.5" />
            Copiar Token API
          </Button>
          <a
            href="/blissf-mail-api.md"
            download="blissf-mail-api.md"
            className="flex items-center gap-1.5 rounded-lg border border-primary/40 bg-primary/5 px-3 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-primary/10"
            title="Baixar a documentação da API (endpoints, exemplos de código e fluxo de automação)"
          >
            <FileText className="h-3.5 w-3.5" />
            Doc da API
          </a>
        </div>
      </div>

      {/* Retention notice */}
      <div className="mx-4 mb-2 flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-1.5 text-[11px] text-muted-foreground shrink-0">
        <Clock className="h-3.5 w-3.5 text-primary shrink-0" />
        Mensagens expiram em 7 dias.
      </div>

      {/* 3-column layout — stacks on small screens */}
      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[280px_340px_1fr] gap-3 px-4 pb-4">
        {/* ---- Column 1: Inboxes ---- */}
        <div className="flex flex-col min-h-0 rounded-xl border border-border bg-card">
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-border shrink-0">
            <span className="text-xs font-semibold text-foreground">Caixas</span>
            <Button size="sm" className="h-7 gap-1 text-xs" onClick={() => setCreateOpen(true)}>
              <Plus className="h-3.5 w-3.5" /> Nova caixa
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {inboxesLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-14 w-full" />
                <Skeleton className="h-14 w-full" />
              </div>
            ) : inboxes.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-center text-muted-foreground">
                <InboxIcon className="h-8 w-8 mb-2 opacity-50" />
                <p className="text-xs">Nenhuma caixa ainda.</p>
                <p className="text-[11px]">Crie uma para começar a receber emails.</p>
              </div>
            ) : (
              inboxes.map((inbox) => (
                <button
                  key={inbox.id}
                  onClick={() => {
                    setSelectedInboxId(inbox.id)
                    setSelectedMessageId(null)
                  }}
                  className={cn(
                    'group w-full rounded-lg px-3 py-2 text-left transition-colors',
                    selectedInboxId === inbox.id
                      ? 'bg-sidebar-accent'
                      : 'hover:bg-secondary',
                  )}
                >
                  <div className="flex items-center gap-2">
                    <span className="min-w-0 flex-1 truncate text-xs font-medium text-foreground">
                      {inbox.label || inbox.address}
                    </span>
                    {inbox.unread_count ? (
                      <Badge variant="destructive" className="h-4 px-1.5 text-[9px]">
                        {inbox.unread_count > 99 ? '99+' : inbox.unread_count}
                      </Badge>
                    ) : null}
                  </div>
                  <div className="mt-0.5 flex items-center gap-1">
                    <span className="min-w-0 flex-1 truncate text-[10px] text-muted-foreground">
                      {inbox.address}
                    </span>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation()
                        copy(inbox.address, 'Endereço copiado')
                      }}
                      className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground opacity-0 transition-opacity hover:text-primary group-hover:opacity-100"
                      title="Copiar endereço"
                    >
                      <Copy className="h-3 w-3" />
                    </span>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation()
                        setDeleteTarget(inbox)
                      }}
                      className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
                      title="Apagar caixa"
                    >
                      <Trash2 className="h-3 w-3" />
                    </span>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* ---- Column 2: Messages ---- */}
        <div className="flex flex-col min-h-0 rounded-xl border border-border bg-card">
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-border shrink-0">
            <span className="min-w-0 truncate text-xs font-semibold text-foreground">
              {selectedInbox ? selectedInbox.address : 'Mensagens'}
            </span>
            {selectedInbox && (
              <button
                onClick={() => copy(selectedInbox.address, 'Endereço copiado')}
                className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-primary shrink-0"
                title="Copiar endereço"
              >
                <Copy className="h-3 w-3" /> copiar
              </button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {!selectedInbox ? (
              <div className="flex h-full items-center justify-center text-center text-xs text-muted-foreground p-6">
                Selecione uma caixa
              </div>
            ) : messagesLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
              </div>
            ) : messages.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center text-center text-muted-foreground p-6">
                <RefreshCw className="h-6 w-6 mb-2 opacity-50" />
                <p className="text-xs">Nenhuma mensagem.</p>
                <p className="text-[11px]">Aguardando emails para este endereço.</p>
              </div>
            ) : (
              messages.map((m) => (
                <button
                  key={m.id}
                  onClick={() => setSelectedMessageId(m.id)}
                  className={cn(
                    'w-full rounded-lg px-3 py-2 text-left transition-colors',
                    selectedMessageId === m.id ? 'bg-sidebar-accent' : 'hover:bg-secondary',
                  )}
                >
                  <div className="flex items-center gap-2">
                    {!m.read_at && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />}
                    <span
                      className={cn(
                        'min-w-0 flex-1 truncate text-xs',
                        m.read_at ? 'text-muted-foreground' : 'font-semibold text-foreground',
                      )}
                    >
                      {m.from_name || m.from_address}
                    </span>
                    <span className="shrink-0 text-[10px] text-muted-foreground">
                      {formatWhen(m.received_at)}
                    </span>
                  </div>
                  <div className="mt-0.5 truncate text-[11px] text-muted-foreground">
                    {m.subject || '(sem assunto)'}
                  </div>
                  <div className="mt-1 flex items-center gap-2">
                    {m.auth_code && (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation()
                          copy(m.auth_code!, `Código ${m.auth_code} copiado`)
                        }}
                        className="inline-flex items-center gap-1 rounded-md border border-primary/40 bg-primary/10 px-1.5 py-0.5 text-[11px] font-mono font-semibold text-primary hover:bg-primary/20"
                        title="Copiar código"
                      >
                        <KeyRound className="h-3 w-3" /> {m.auth_code}
                      </span>
                    )}
                    {m.has_attachments && (
                      <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                        <Paperclip className="h-3 w-3" /> anexo
                      </span>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* ---- Column 3: Reading ---- */}
        <div className="flex flex-col min-h-0 rounded-xl border border-border bg-card">
          {!selectedMessageId ? (
            <div className="flex h-full flex-col items-center justify-center text-center text-muted-foreground p-8">
              <Mail className="h-10 w-10 mb-3 opacity-40" />
              <p className="text-sm">Selecione uma mensagem para ler.</p>
            </div>
          ) : messageLoading || !message ? (
            <div className="flex h-full items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              <div className="border-b border-border px-4 py-3 shrink-0">
                <h2 className="text-sm font-semibold text-foreground break-words">
                  {message.subject || '(sem assunto)'}
                </h2>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                  <span>
                    {message.from_name ? `${message.from_name} · ` : ''}
                    {message.from_address}
                  </span>
                  <span>{new Date(message.received_at).toLocaleString('pt-BR')}</span>
                </div>
                {message.auth_code && (
                  <button
                    onClick={() => copy(message.auth_code!, `Código ${message.auth_code} copiado`)}
                    className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-primary/40 bg-primary/10 px-2 py-1 text-xs font-mono font-semibold text-primary hover:bg-primary/20"
                    title="Copiar código"
                  >
                    <KeyRound className="h-3.5 w-3.5" /> {message.auth_code}
                  </button>
                )}
              </div>

              <div className="flex-1 overflow-y-auto min-h-0">
                {message.html_body ? (
                  <iframe
                    title="Conteúdo do email"
                    sandbox=""
                    srcDoc={message.html_body}
                    className="h-full w-full border-0 bg-white"
                  />
                ) : (
                  <pre className="whitespace-pre-wrap break-words p-4 text-xs text-foreground font-sans">
                    {message.text_body || '(mensagem vazia)'}
                  </pre>
                )}
              </div>

              {message.attachment_names && message.attachment_names.length > 0 && (
                <div className="border-t border-border px-4 py-2 shrink-0">
                  <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    <Paperclip className="h-3.5 w-3.5" />
                    <span className="font-medium text-foreground">
                      {message.attachment_names.join(', ')}
                    </span>
                  </div>
                  <p className="mt-0.5 text-[10px] text-muted-foreground/80">
                    Anexos descartados — apenas os nomes são registrados.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ---- New inbox dialog ---- */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nova caixa</DialogTitle>
            <DialogDescription>
              Crie um endereço @blissf.com para receber emails.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Mode toggle */}
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setCreateMode('random')}
                className={cn(
                  'rounded-lg border px-3 py-2 text-xs font-medium transition-colors',
                  createMode === 'random'
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground hover:bg-secondary',
                )}
              >
                Aleatória
              </button>
              <button
                type="button"
                onClick={() => setCreateMode('custom')}
                className={cn(
                  'rounded-lg border px-3 py-2 text-xs font-medium transition-colors',
                  createMode === 'custom'
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground hover:bg-secondary',
                )}
              >
                Escolher endereço
              </button>
            </div>

            {createMode === 'custom' && (
              <div className="space-y-1.5">
                <Label className="text-xs">Endereço</Label>
                <div className="flex items-center gap-2">
                  <Input
                    value={customLocal}
                    onChange={(e) => setCustomLocal(e.target.value)}
                    placeholder="meu-nome"
                    autoFocus
                  />
                  <span className="text-xs text-muted-foreground shrink-0">@blissf.com</span>
                </div>
                <p className="text-[10px] text-muted-foreground">
                  3 a 64 caracteres: letras, números, . _ -
                </p>
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-xs">Rótulo (opcional)</Label>
              <Input
                value={customLabel}
                onChange={(e) => setCustomLabel(e.target.value)}
                placeholder="Ex.: Cadastros"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={creating}>
              Cancelar
            </Button>
            <Button onClick={handleCreate} disabled={creating}>
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Criar caixa'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- Delete confirm dialog ---- */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Apagar caixa?</DialogTitle>
            <DialogDescription>
              A caixa <span className="font-medium text-foreground">{deleteTarget?.address}</span> e
              todas as suas mensagens serão removidas. Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apagar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
